import { useRef } from "react";
import { motion, useScroll, useTransform, useSpring } from "motion/react";
import { Link } from "@tanstack/react-router";
import { ArrowRight } from "lucide-react";
import { roofingMaterials } from "@/components/RoofingMaterials";

type Pan = { from: { scale: number; x: string; y: string }; to: { scale: number; x: string; y: string } };

const PANS: Pan[] = [
  { from: { scale: 1.08, x: "-2%", y: "-1%" }, to: { scale: 1.16, x: "2%", y: "2%" } },
  { from: { scale: 1.12, x: "2%", y: "1%" }, to: { scale: 1.02, x: "-2%", y: "-2%" } },
  { from: { scale: 1.05, x: "1%", y: "-2%" }, to: { scale: 1.14, x: "-2%", y: "2%" } },
  { from: { scale: 1.06, x: "-3%", y: "1%" }, to: { scale: 1.18, x: "3%", y: "-2%" } },
  { from: { scale: 1.04, x: "2%", y: "-1%" }, to: { scale: 1.12, x: "-2%", y: "2%" } },
  { from: { scale: 1.1, x: "-1%", y: "2%" }, to: { scale: 1.02, x: "2%", y: "-1%" } },
  { from: { scale: 1.03, x: "2%", y: "2%" }, to: { scale: 1.15, x: "-2%", y: "-2%" } },
];

const STEPS = roofingMaterials.map((m, i) => ({
  index: String(i + 1).padStart(2, "0"),
  eyebrow: "Materials we install",
  title: m.name,
  body: m.blurb,
  meta: `Best for: ${m.bestFor}`,
  image: m.image ?? "",
  alt: m.alt ?? m.name,
  href: m.href,
  ...PANS[i % PANS.length],
}));

export function RoofingMaterialsScroll({
  heading = "Materials We Install",
}: {
  heading?: string;
}) {
  const sectionRef = useRef<HTMLElement>(null);
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start start", "end end"],
  });

  const COUNT = STEPS.length;
  const HYSTERESIS = 0.015;
  const lastIdxRef = useRef(0);
  const snapped = useTransform(scrollYProgress, (p) => {
    let idx = lastIdxRef.current;
    while (idx < COUNT - 1 && p > (idx + 1) / COUNT + HYSTERESIS) idx++;
    while (idx > 0 && p < idx / COUNT - HYSTERESIS) idx--;
    lastIdxRef.current = idx;
    return idx;
  });
  const smooth = useSpring(snapped, { stiffness: 110, damping: 24, mass: 0.4 });

  return (
    <>
      {/* Desktop: pinned scroll animation */}
      <section
        ref={sectionRef}
        className="relative hidden bg-primary text-primary-foreground lg:block"
        style={{ height: `${COUNT * 62}vh` }}
        aria-label="Roofing materials we install"
      >
        <div className="sticky top-0 h-screen w-full overflow-hidden">
          <div className="pointer-events-none absolute inset-x-0 top-0 z-20 px-6 pt-20 sm:px-16 sm:pt-24 lg:pt-28">
            <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-primary-foreground/80 sm:text-[11px]">
              Roofing systems
            </p>
            <h2 className="mt-2 max-w-2xl font-serif text-2xl font-bold tracking-tight text-balance sm:mt-3 sm:text-4xl lg:text-5xl">
              {heading}
            </h2>
          </div>

          <div className="absolute inset-0 z-10 grid grid-cols-1 lg:grid-cols-[1fr_1.15fr]">
            {/* Copy column */}
            <div className="relative flex items-center px-6 pb-16 pt-48 sm:px-16 sm:pb-24 sm:pt-56 lg:pt-48 xl:pt-56">
              <div className="relative w-full max-w-xl">
                <div className="mb-8 flex items-center gap-3">
                  {STEPS.map((_, i) => (
                    <TickMark key={i} index={i} progress={smooth} />
                  ))}
                </div>
                <div className="relative min-h-[380px] sm:min-h-[400px]">
                  {STEPS.map((s, i) => (
                    <StepCopy key={i} step={s} index={i} progress={smooth} />
                  ))}
                </div>
              </div>
            </div>

            {/* Image column */}
            <div
              className="relative hidden overflow-hidden lg:block"
              style={{
                WebkitMaskImage:
                  "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 8%, #000 26%)",
                maskImage:
                  "linear-gradient(to right, transparent 0%, rgba(0,0,0,0.35) 8%, #000 26%)",
              }}
            >
              {STEPS.map((s, i) => (
                <StepImage key={i} step={s} index={i} progress={smooth} />
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mobile / Tablet: static card grid */}
      <section className="bg-primary py-16 text-primary-foreground lg:hidden" aria-label="Roofing materials we install">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <p className="text-[10px] font-semibold uppercase tracking-[0.32em] text-primary-foreground/80">
            Roofing systems
          </p>
          <h2 className="mt-2 max-w-2xl font-serif text-2xl font-bold tracking-tight text-balance sm:text-3xl">
            {heading}
          </h2>
          <div className="mt-10 grid gap-6 sm:grid-cols-2">
            {STEPS.map((s, i) => (
              <div
                key={i}
                className="group relative overflow-hidden rounded-2xl bg-primary-foreground/5 ring-1 ring-primary-foreground/10"
              >
                <div className="aspect-[4/3] overflow-hidden">
                  <img
                    src={s.image}
                    alt={s.alt}
                    loading="lazy"
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                </div>
                <div className="p-5 sm:p-6">
                  <p className="text-[10px] font-semibold uppercase tracking-[0.28em] text-primary-foreground/80">
                    {s.index} · {s.eyebrow}
                  </p>
                  <h3 className="mt-2 font-serif text-xl font-bold tracking-tight text-primary-foreground text-balance sm:text-2xl">
                    {s.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-primary-foreground/70">
                    {s.body}
                  </p>
                  <p className="mt-4 text-[10px] font-semibold uppercase tracking-[0.28em] text-primary-foreground/50">
                    {s.meta}
                  </p>
                  <div className="mt-5 flex flex-wrap items-center gap-4">
                    <Link
                      to="/contact"
                      className="inline-flex items-center gap-2 rounded-full bg-accent px-5 py-2.5 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent/90"
                    >
                      Get Free Quote
                      <ArrowRight className="h-4 w-4" />
                    </Link>
                    {s.href ? (
                      <Link
                        to={s.href}
                        className="text-sm font-medium text-primary-foreground/70 underline-offset-4 transition-colors hover:text-primary-foreground hover:underline"
                      >
                        Learn more<span className="sr-only"> about {s.title}</span>
                      </Link>
                    ) : null}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}

function TickMark({ index, progress }: { index: number; progress: ReturnType<typeof useSpring> }) {
  const w = useTransform(progress, (v) => (Math.abs(v - index) < 0.5 ? 36 : 14));
  const o = useTransform(progress, (v) => (Math.abs(v - index) < 0.5 ? 1 : 0.3));
  return <motion.span style={{ width: w, opacity: o }} className="h-[2px] rounded-full bg-secondary" />;
}

function StepCopy({
  step,
  index,
  progress,
}: {
  step: (typeof STEPS)[number];
  index: number;
  progress: ReturnType<typeof useSpring>;
}) {
  const opacity = useTransform(progress, (v) => {
    const d = Math.abs(v - index);
    if (d >= 0.7) return 0;
    return Math.max(0, 1 - d / 0.55);
  });
  const y = useTransform(progress, (v) => `${(v - index) * 22}px`);
  const pointerEvents = useTransform(progress, (v) =>
    Math.abs(v - index) < 0.5 ? "auto" : "none",
  );

  return (
    <motion.div style={{ opacity, y, pointerEvents }} className="absolute inset-0">
      <p className="text-xs font-semibold uppercase tracking-[0.28em] text-primary-foreground/80 sm:text-sm">
        {step.index} · {step.eyebrow}
      </p>
      <h3 className="mt-4 font-serif text-3xl font-bold tracking-tight text-primary-foreground text-balance sm:text-4xl lg:text-[2.5rem]">
        {step.title}
      </h3>
      <p className="mt-4 max-w-md text-sm leading-relaxed text-primary-foreground/70 sm:text-base">
        {step.body}
      </p>
      <p className="mt-6 text-[10px] font-semibold uppercase tracking-[0.28em] text-primary-foreground/50 sm:text-[11px]">
        {step.meta}
      </p>
      <div className="mt-7 flex flex-wrap items-center gap-x-6 gap-y-3">
        <Link
          to="/contact"
          className="inline-flex items-center gap-2 rounded-full bg-accent px-6 py-3 text-sm font-semibold text-accent-foreground transition-colors hover:bg-accent/90"
        >
          Get Free Quote
          <span aria-hidden="true">↗</span>
        </Link>
        {step.href ? (
          <Link
            to={step.href}
            className="text-sm font-medium text-primary-foreground/70 underline-offset-4 transition-colors hover:text-primary-foreground hover:underline"
          >
            Learn more<span className="sr-only"> about {step.title}</span>
          </Link>
        ) : null}
      </div>
    </motion.div>
  );
}

function StepImage({
  step,
  index,
  progress,
}: {
  step: (typeof STEPS)[number];
  index: number;
  progress: ReturnType<typeof useSpring>;
}) {
  const opacity = useTransform(progress, (v) => {
    const d = Math.abs(v - index);
    if (d >= 1) return 0;
    return 1 - d;
  });
  const t = useTransform(progress, (v) => {
    const local = v - index + 0.5;
    return Math.max(0, Math.min(1, local));
  });
  const scale = useTransform(t, [0, 1], [step.from.scale, step.to.scale]);
  const x = useTransform(t, [0, 1], [step.from.x, step.to.x]);
  const y = useTransform(t, [0, 1], [step.from.y, step.to.y]);

  return (
    <motion.div style={{ opacity }} className="absolute inset-0 will-change-[opacity]">
      <motion.img
        src={step.image}
        alt={step.alt}
        loading="lazy"
        width={1800}
        height={1200}
        style={{ scale, x, y }}
        className="h-full w-full object-cover will-change-transform"
      />
    </motion.div>
  );
}
